// / — landing page once logged in. Shows the /me payload + a logout button.
//
// If /me returns 401 we bounce to /login. We do this once on mount; no
// background polling. The cookie's 24h TTL is long enough that a single
// hard navigation will catch expiry.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, api, type Me } from "../lib/api";

export function HomePage() {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((data) => {
        if (!cancelled) {
          setMe(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load user");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function onLogout() {
    try {
      await api.logout();
    } catch {
      // Even if logout fails, clear local state and bounce.
    }
    navigate("/login", { replace: true });
  }

  if (loading) {
    return (
      <div className="page">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="card">
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <nav className="nav">
        <Link to="/">Home</Link>
        <Link to="/hosts">Hosts</Link>
      </nav>
      <div className="card">
        <div className="toolbar">
          <h1 style={{ margin: 0, fontSize: 20 }}>Welcome, {me?.display_name}</h1>
          <button className="secondary" onClick={onLogout} data-testid="logout">
            Log out
          </button>
        </div>
        <dl style={{ margin: 0 }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 6 }}>
            <dt className="muted" style={{ width: 100 }}>
              Email
            </dt>
            <dd style={{ margin: 0 }} data-testid="me-email">
              {me?.email}
            </dd>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 6 }}>
            <dt className="muted" style={{ width: 100 }}>
              Role
            </dt>
            <dd style={{ margin: 0 }} data-testid="me-role">
              {me?.role}
            </dd>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <dt className="muted" style={{ width: 100 }}>
              User ID
            </dt>
            <dd style={{ margin: 0, fontFamily: "monospace", fontSize: 13 }}>{me?.user_id}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
